// Admin-only screen-space grid overlay state. Persisted in localStorage
// because it's a per-device UI preference for tuning sessions — not
// game state, not user-preference-worthy-of-syncing.
//
// useGridOverlay() returns [enabled, setEnabled] with reactive updates
// so the toggle button in PhysicsTuner and the overlay renderer in
// GameSurface stay in sync without prop drilling.

import { useEffect, useState } from "react";

const STORAGE_KEY = "cornholio.admin.gridOverlay";

function readEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeEnabled(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value ? "1" : "0");
    // Custom event so listeners in this tab pick up the change. The
    // browser's "storage" event only fires across tabs, not within
    // the same one.
    window.dispatchEvent(new CustomEvent("cornholio:grid-overlay-changed"));
  } catch {
    // ignore
  }
}

export function useGridOverlay(): [boolean, (next: boolean) => void] {
  const [enabled, setEnabled] = useState(readEnabled);
  useEffect(() => {
    const refresh = () => setEnabled(readEnabled());
    window.addEventListener("storage", refresh);
    window.addEventListener("cornholio:grid-overlay-changed", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("cornholio:grid-overlay-changed", refresh);
    };
  }, []);
  const set = (next: boolean) => {
    writeEnabled(next);
    setEnabled(next);
  };
  return [enabled, set];
}
